import React, { useState, useEffect, useRef } from 'react';
import { getAuth } from 'firebase/auth';
import { 
  Shield, ShieldCheck, Briefcase, FileText, MessageSquare, Settings, Search, Bell, User, Lock, LogOut, MoreVertical, 
  Download, Upload, Plus, CheckCircle2, Clock, Eye, Layers, X, ChevronRight, ChevronLeft, 
  FilePlus2, ArrowUp, ArrowDown, CheckSquare, CalendarDays, ArrowRight, Edit, Trash2, Save, Calendar, 
  MapPin, Gavel, Users, Receipt, Banknote, RefreshCw, Mail, Sliders, ArrowDownAZ, Image as ImageIcon, FileEdit, 
  MoveUp, MoveDown, RotateCw, Settings2, Database, UserCog, Scan, Printer, MonitorDown, Copy, FolderSync, Menu, Paperclip, ExternalLink, Folder, FolderOpen, UploadCloud
} from 'lucide-react';
import { ROLE_NAMES, formatHebDate, handleDownloadLocalFile } from '../utils';

export default function ScannerTool({ cases, clients, dbOps, onBack }) {
  const [scannedDocs, setScannedDocs] = useState([]);
  const [selectedClientMap, setSelectedClientMap] = useState({});
  const [selectedCaseMap, setSelectedCaseMap] = useState({});
  const folderUrl = "\\\\LAW-SERVER\\Scans\\Incoming";

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const newDocs = files.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      pages: 1, 
      scannedBy: getAuth().currentUser?.displayName || 'אני (עו"ד)',
      date: new Date().toISOString().split('T')[0],
      scannerName: 'סריקה מתיקיית רשת'
    }));
    
    setScannedDocs([...newDocs, ...scannedDocs]);
    e.target.value = null; 
  };

  const simulateIncoming = () => {
    const now = new Date();
    const timeStr = `${String(now.getHours()).padStart(2,'0')}${String(now.getMinutes()).padStart(2,'0')}`;
    const newDoc = {
      id: Date.now() + Math.random(),
      name: `מסמך_סרוק_${timeStr}.pdf`,
      pages: Math.floor(Math.random() * 8) + 1,
      scannedBy: 'סורק רשת',
      date: now.toISOString().split('T')[0],
      scannerName: 'תיקיית סריקות'
    };
    setScannedDocs([newDoc, ...scannedDocs]);
  };

  const handleSaveToCase = async (docObj) => {
    const caseId = selectedCaseMap[docObj.id];
    if (!caseId) {
      alert("אנא בחר תיק לשמירה מתוך הרשימה");
      return;
    }
    
    const dbDoc = {
      name: docObj.name,
      caseId: caseId,
      uploadedBy: docObj.scannedBy,
      date: docObj.date,
      size: `${(docObj.pages * 0.4).toFixed(1)} MB`,
      securityLevel: 'רגיל'
    };
    
    await dbOps.handleSave('documents', dbDoc);
    setScannedDocs(scannedDocs.filter(d => d.id !== docObj.id));
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(folderUrl).catch(() => {
      const textArea = document.createElement("textarea");
      textArea.value = folderUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      textArea.remove();
    });
    alert('כתובת התיקייה הועתקה ללוח!');
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 h-full flex flex-col overflow-hidden animate-in fade-in duration-300">
      <div className="p-4 border-b border-slate-200 bg-slate-50/50 flex items-center gap-4">
        <button onClick={onBack} className="p-2 bg-white border border-slate-200 text-slate-500 rounded-lg hover:text-purple-600 transition-colors"><ArrowRight className="w-5 h-5" /></button>
        <div><h3 className="font-bold text-lg text-slate-800 flex items-center gap-2"><Scan className="w-5 h-5 text-purple-600" /> סריקת מסמכים ישירות מהרשת</h3></div>
      </div>
      
      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        <div className="w-full md:w-80 bg-slate-50 border-l border-slate-200 p-6 flex flex-col items-center border-b md:border-b-0 overflow-y-auto">
          <div className="text-center w-full">
             <div className="w-24 h-24 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                <FolderSync className="w-10 h-10 text-purple-600" />
             </div>
             <h4 className="font-bold text-slate-800 text-lg">תיקיית סריקות ברשת</h4>
             <p className="text-sm text-slate-500 mt-2 mb-4">
               כדי לסרוק מסמכים ישירות למערכת, הגדר את סורק המשרד לשמור קבצים בכתובת הבאה:
             </p>
             
             <div className="bg-white p-3 rounded-lg flex items-center justify-between mb-8 border border-slate-300 shadow-sm gap-2">
                <span className="font-mono text-sm text-slate-800 font-bold truncate" dir="ltr">{folderUrl}</span>
                <button onClick={copyToClipboard} className="text-purple-600 hover:text-purple-800 shrink-0 bg-purple-50 p-1.5 rounded-md transition-colors" title="העתק כתובת">
                  <Copy className="w-4 h-4"/>
                </button>
             </div>
             
             <div className="space-y-3 mt-auto pt-4 border-t border-slate-200">
               <p className="text-xs text-slate-400 font-medium mb-2">פעולות להדגמה / העלאה ידנית:</p>
               
               <label className="w-full py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all shadow-sm bg-purple-600 hover:bg-purple-700 text-white cursor-pointer hover:-translate-y-0.5">
                  <Upload className="w-4 h-4" />
                  טען קבצים מהתיקייה ידנית
                  <input type="file" multiple className="hidden" onChange={handleFileUpload} accept=".pdf,.jpg,.png,.tiff" />
               </label>
               
               <button onClick={simulateIncoming} className="w-full py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all border border-purple-200 text-purple-700 hover:bg-purple-50 bg-white">
                  <RefreshCw className="w-4 h-4" />
                  דמה סריקה נכנסת למערכת
               </button>
             </div>
          </div>
        </div>

        <div className="flex-1 p-4 md:p-8 overflow-y-auto bg-slate-100/30">
          <div className="max-w-4xl mx-auto">
            <h3 className="font-bold text-slate-800 mb-6 flex items-center gap-2">
              <Layers className="w-5 h-5 text-slate-400" /> קבצים שנסרקו ממתינים לפעולה ({scannedDocs.length})
            </h3>
            
            {scannedDocs.length === 0 ? (
              <div className="h-64 border-2 border-dashed border-slate-300 rounded-2xl flex flex-col items-center justify-center text-slate-400 bg-white">
                 <FolderSync className="w-12 h-12 mb-3 opacity-30" />
                 <p className="font-medium text-slate-500">התיקייה ריקה. סרוק מסמך לתיקיית הרשת.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {scannedDocs.map(docObj => (
                  <div key={docObj.id} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm flex flex-col lg:flex-row lg:items-center justify-between gap-6 animate-in fade-in slide-in-from-bottom-2">
                    <div className="flex items-center gap-4">
                       <div className="w-12 h-12 bg-red-50 text-red-600 rounded-lg flex items-center justify-center shrink-0">
                         <FileText className="w-6 h-6" />
                       </div>
                       <div>
                         <h4 className="font-bold text-slate-800">{docObj.name}</h4>
                         <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mt-1">
                            <span className="bg-slate-100 px-2 py-0.5 rounded font-medium">{docObj.pages} עמודים</span>
                            <span title={`מקור: ${docObj.scannerName}`}>נמשך ע"י: {docObj.scannedBy}</span>
                            <span dir="ltr">{formatHebDate(docObj.date)}</span>
                         </div>
                       </div>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0 border-t lg:border-t-0 border-slate-100 pt-4 lg:pt-0">
                      <select 
                        className="p-2.5 text-sm border border-slate-300 rounded-lg bg-slate-50 focus:bg-white outline-none min-w-[150px]"
                        value={selectedClientMap[docObj.id] || ''}
                        onChange={(e) => {
                          setSelectedClientMap({...selectedClientMap, [docObj.id]: e.target.value});
                          setSelectedCaseMap({...selectedCaseMap, [docObj.id]: ''}); // Reset case when client changes
                        }}
                      >
                        <option value="" disabled>-- 1. בחר לקוח --</option>
                        {clients.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
                      </select>
                      
                      <select 
                        className="p-2.5 text-sm border border-slate-300 rounded-lg bg-slate-50 focus:bg-white outline-none min-w-[150px]"
                        value={selectedCaseMap[docObj.id] || ''}
                        onChange={(e) => setSelectedCaseMap({...selectedCaseMap, [docObj.id]: e.target.value})}
                        disabled={!selectedClientMap[docObj.id]}
                      >
                        <option value="" disabled>-- 2. בחר תיק לשמירה --</option>
                        {cases.filter(c => c.client === selectedClientMap[docObj.id]).map(c => <option key={c._id} value={c.id}>{c.id} - {c.title}</option>)}
                      </select>
                      
                      <button 
                        onClick={() => handleSaveToCase(docObj)}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors shadow-sm"
                      >
                        <Database className="w-4 h-4" /> בענן
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}